/**
 * inline_line_items_controller.js — click-to-edit line items in place (Stimulus).
 *
 * Used by the shared line items block (components/detail/entity_line_items.html)
 * on quote detail (and later job/invoice) pages. The pencil (or the "Add line
 * items" empty state) swaps the read-only tables for the shared LineItemsManager
 * UI — the same engine the edit modal uses (static/js/utils/line-items-manager.js)
 * — lazily mounted on first edit. Save POSTs only line_items_json +
 * use_line_items + show_line_items through window.TurboEditModal; the update
 * endpoint's sentinels preserve every other field and re-render this block (and
 * the dependent amount/data partials) as Turbo Stream replaces, which swaps
 * back to display mode with a fresh controller.
 *
 * Seeding: the editor loads from the page's hidden #{entityType}Data div
 * (e.g. #quoteData, data-line-items / data-show-line-items) — the SSOT
 * serialization the edit modal also reads, re-rendered after every save — so
 * cancel can simply discard editor state and the next edit re-seeds.
 *
 * Element IDs inside the form are prefixed 'inline{EntityType}' (inlineQuote...)
 * and the manager's global row handlers get the same prefix
 * (window.addInlineQuoteLineItem, ...), keeping them clear of the edit modal's
 * editQuote-prefixed manager on the same page.
 *
 * Config (data-* on the controller root):
 *   data-inline-line-items-url          POST target (the entity's update endpoint)
 *   data-inline-line-items-entity-type  'quote' | 'job' | 'invoice'
 *   data-currency-symbol / data-tax-name / data-tax-rate
 *                                       display config (mirrors the edit modal root)
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this.url = this.element.dataset.inlineLineItemsUrl;
            this.entityType = this.element.dataset.inlineLineItemsEntityType || 'item';
            this.prefix = 'inline' + this.entityType.charAt(0).toUpperCase() + this.entityType.slice(1);
            this.display = this.element.querySelector('.inline-edit-display');
            this.form = this.element.querySelector('.inline-edit-form');
            this.manager = null;

            // Close the editor before Turbo caches the page so the snapshot
            // matches the server render (mirrors inline_edit_controller).
            this._onBeforeCache = () => this._showDisplay();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        disconnect() {
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
        }

        edit() {
            if (!this.form || !this._mountManager()) return;
            this._seed();
            // An empty editor starts with one blank row ready to fill in.
            if (!this.manager.hasItems()) this.manager.add(null, { highlight: false });
            this.display.classList.add('d-none');
            this.form.classList.remove('d-none');
        }

        addItem() {
            if (this.manager) this.manager.add();
        }

        // The manager auto-checks the box when meaningful items exist, but a
        // manual toggle must sync the hidden input it submits from.
        syncShowCheck(event) {
            const hidden = document.getElementById(`${this.prefix}ShowLineItems`);
            if (hidden) hidden.value = event.target.checked ? 'true' : 'false';
        }

        cancel() {
            // Editor state is disposable — the next edit re-seeds from the
            // entity data div, which only changes on a successful save.
            this._showDisplay();
        }

        async save(event) {
            event.preventDefault();
            if (!this.manager || !window.TurboEditModal) return;
            // use_line_items mirrors whether any row carries real data at save
            // time: all-blank (or none) submits 'false', which clears the
            // breakdown server-side. Matches the edit modal's collapse path.
            const useInput = document.getElementById(`${this.prefix}UseLineItems`);
            if (useInput) useInput.value = this.manager.hasMeaningfulItems() ? 'true' : 'false';
            // A formError stream targets the modal's hidden error region —
            // invisible here — so surface it as a toast instead and leave the
            // editor open for retry.
            await window.TurboEditModal.submit(this.form, {
                entityType: this.entityType,
                url: this.url,
                onError: (err) => {
                    if (err && err.formError) {
                        window.TurboEditModal.showNotification(
                            'Those line items were not accepted. Please try again.',
                            'error'
                        );
                    }
                },
            });
        }

        // Lazily instantiate the shared LineItemsManager against this form's
        // prefixed elements. The amount/locked-indicator element IDs are
        // deliberately omitted — the manager null-guards them, and the amount
        // partial re-renders from the server after save instead.
        _mountManager() {
            if (this.manager) return this.manager;
            if (typeof window.LineItemsManager === 'undefined') {
                console.warn('[InlineLineItems] LineItemsManager not loaded');
                return null;
            }
            const ds = this.element.dataset;
            const p = this.prefix;
            this.manager = new window.LineItemsManager({
                containerId: `${p}LineItemsContainer`,
                totalsId: `${p}LineItemsTotals`,
                rowIdPrefix: `${p}LineItem_`,
                globalFunctionPrefix: p,
                currencySymbol: ds.currencySymbol ?? '$',
                taxName: ds.taxName ?? 'GST',
                defaultTaxRate: ds.taxRate ?? 10,
                vatRates: ds.vatRates,
                taxExempt: ds.taxExempt,
                amountsAre: ds.amountsAre,
                elements: {
                    subtotal: `${p}LineItemsSubtotal`,
                    tax: `${p}LineItemsTax`,
                    total: `${p}LineItemsTotal`,
                    lineItemsJson: `${p}LineItemsJson`,
                    useLineItems: `${p}UseLineItems`,
                    showLineItems: `${p}ShowLineItems`,
                    showLineItemsCheck: `${p}ShowLineItemsCheck`,
                },
                defaultQuantity: '',
            });
            return this.manager;
        }

        _seed() {
            const seedEl = document.getElementById(`${this.entityType}Data`);
            let items = [];
            let show = false;
            if (seedEl) {
                try {
                    items = JSON.parse(seedEl.dataset.lineItems || '[]');
                } catch (_e) {
                    items = [];
                }
                show = seedEl.dataset.showLineItems === 'true';
            }
            this.manager.load(items, show);
        }

        _showDisplay() {
            if (this.form) this.form.classList.add('d-none');
            if (this.display) this.display.classList.remove('d-none');
        }
}
